'use client';

import { c as _c } from "react/compiler-runtime";
import { useState, useCallback, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { X, Send, CheckCircle2, Loader2 } from 'lucide-react';
import TurnstileWidget from '@/components/auth/TurnstileWidget';
interface ContactFormModalProps {
  open: boolean;
  onClose: () => void;
}
export default function ContactFormModal({
  open,
  onClose
}: ContactFormModalProps) {
  const [form, setForm] = useState({
    firstName: '',
    lastName: '',
    email: '',
    phone: '',
    company: '',
    message: ''
  });
  const [captchaToken, setCaptchaToken] = useState<string | null>(null);
  const [turnstileResetKey, setTurnstileResetKey] = useState(0);
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const handleChange = (field: string, value: string) => {
    setForm(prev => ({
      ...prev,
      [field]: value
    }));
  };
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!captchaToken) return;
    setSubmitting(true);
    setError(null);
    try {
      const res = await fetch('/api/contact', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          ...form,
          captchaToken
        })
      });
      const data = await res.json();
      if (!res.ok) {
        throw new Error(data.error || 'Failed to submit form');
      }
      setSubmitted(true);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Something went wrong');
      setTurnstileResetKey(k => k + 1);
      setCaptchaToken(null);
    } finally {
      setSubmitting(false);
    }
  };
  const handleClose = () => {
    if (!submitting) {
      setForm({
        firstName: '',
        lastName: '',
        email: '',
        phone: '',
        company: '',
        message: ''
      });
      setCaptchaToken(null);
      setSubmitted(false);
      setError(null);
      setTurnstileResetKey(k_0 => k_0 + 1);
      onClose();
    }
  };
  const handleTurnstileSuccess = useCallback((token: string) => {
    setCaptchaToken(token);
  }, []);
  const [mounted, setMounted] = useState(false);
  useEffect(() => {
    setMounted(true);
  }, []);
  if (!open || !mounted) return null;
  return createPortal(<div className="fixed inset-0 z-[100] flex items-center justify-center p-4" onClick={handleClose}>
      <div className="absolute inset-0 bg-black/70 backdrop-blur-sm" />
      <div className="relative w-full max-w-lg max-h-[90vh] overflow-y-auto glass-strong rounded-2xl border border-cyan-500/20 shadow-2xl animate-in fade-in zoom-in-95 duration-200" onClick={e_0 => e_0.stopPropagation()}>
        {/* Header */}
        <div className="sticky top-0 z-10 flex items-center justify-between p-6 pb-4 border-b border-white/10 glass-strong rounded-t-2xl">
          <h2 className="text-xl font-bold text-white">Get in Touch</h2>
          <button onClick={handleClose} className="text-white/50 hover:text-white transition-colors p-1" disabled={submitting}>
            <X className="h-5 w-5" />
          </button>
        </div>

        {/* Body */}
        <div className="p-6">
          {submitted ? <SuccessMessage onClose={handleClose} /> : <ContactForm form={form} onChange={handleChange} onSubmit={handleSubmit} captchaToken={captchaToken} onTurnstileSuccess={handleTurnstileSuccess} onTurnstileError={() => setCaptchaToken(null)} turnstileResetKey={turnstileResetKey} submitting={submitting} error={error} />}
        </div>
      </div>
    </div>, document.body);
}

/* ── Success message ──────────────────────────────────────────────── */

function SuccessMessage(t0) {
  const $ = _c(5);
  const {
    onClose
  } = t0;
  let t1;
  let t2;
  let t3;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = <div className="inline-flex p-4 rounded-full bg-gradient-to-br from-cyan-500/20 to-teal-500/20 border border-cyan-500/30"><CheckCircle2 className="h-10 w-10 text-cyan-400" /></div>;
    t2 = <h3 className="text-xl font-semibold text-white">Thank you!</h3>;
    t3 = <p className="text-white/60">We've received your enquiry and will be in touch shortly.</p>;
    $[0] = t1;
    $[1] = t2;
    $[2] = t3;
  } else {
    t1 = $[0];
    t2 = $[1];
    t3 = $[2];
  }
  let t4;
  if ($[3] !== onClose) {
    t4 = <div className="text-center py-8 space-y-4">{t1}{t2}{t3}<button onClick={onClose} className="mt-4 px-6 py-2 rounded-lg bg-gradient-to-r from-cyan-500 to-teal-500 text-white font-medium hover:from-cyan-600 hover:to-teal-600 transition-all">Close</button></div>;
    $[3] = onClose;
    $[4] = t4;
  } else {
    t4 = $[4];
  }
  return t4;
}

/* ── Contact form ─────────────────────────────────────────────────── */

interface ContactFormProps {
  form: Record<string, string>;
  onChange: (field: string, value: string) => void;
  onSubmit: (e: React.FormEvent) => void;
  captchaToken: string | null;
  onTurnstileSuccess: (token: string) => void;
  onTurnstileError: () => void;
  turnstileResetKey: number;
  submitting: boolean;
  error: string | null;
}
function ContactForm(t0) {
  const $ = _c(61);
  const {
    form,
    onChange,
    onSubmit,
    captchaToken,
    onTurnstileSuccess,
    onTurnstileError,
    turnstileResetKey,
    submitting,
    error
  } = t0;
  let t1;
  if ($[0] !== onChange) {
    t1 = v => onChange("firstName", v);
    $[0] = onChange;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  let t2;
  if ($[2] !== form.firstName || $[3] !== submitting || $[4] !== t1) {
    t2 = <FormField label="First Name *" id="firstName" value={form.firstName} onChange={t1} required={true} disabled={submitting} />;
    $[2] = form.firstName;
    $[3] = submitting;
    $[4] = t1;
    $[5] = t2;
  } else {
    t2 = $[5];
  }
  let t3;
  if ($[6] !== onChange) {
    t3 = v_0 => onChange("lastName", v_0);
    $[6] = onChange;
    $[7] = t3;
  } else {
    t3 = $[7];
  }
  let t4;
  if ($[8] !== form.lastName || $[9] !== submitting || $[10] !== t3) {
    t4 = <FormField label="Last Name *" id="lastName" value={form.lastName} onChange={t3} required={true} disabled={submitting} />;
    $[8] = form.lastName;
    $[9] = submitting;
    $[10] = t3;
    $[11] = t4;
  } else {
    t4 = $[11];
  }
  let t5;
  if ($[12] !== t2 || $[13] !== t4) {
    t5 = <div className="grid grid-cols-2 gap-4">{t2}{t4}</div>;
    $[12] = t2;
    $[13] = t4;
    $[14] = t5;
  } else {
    t5 = $[14];
  }
  let t6;
  if ($[15] !== onChange) {
    t6 = v_1 => onChange("email", v_1);
    $[15] = onChange;
    $[16] = t6;
  } else {
    t6 = $[16];
  }
  let t7;
  if ($[17] !== form.email || $[18] !== submitting || $[19] !== t6) {
    t7 = <FormField label="Email Address *" id="email" type="email" value={form.email} onChange={t6} required={true} disabled={submitting} />;
    $[17] = form.email;
    $[18] = submitting;
    $[19] = t6;
    $[20] = t7;
  } else {
    t7 = $[20];
  }
  let t8;
  if ($[21] !== onChange) {
    t8 = v_2 => onChange("phone", v_2);
    $[21] = onChange;
    $[22] = t8;
  } else {
    t8 = $[22];
  }
  let t9;
  if ($[23] !== form.phone || $[24] !== submitting || $[25] !== t8) {
    t9 = <FormField label="Phone Number" id="phone" type="tel" value={form.phone} onChange={t8} disabled={submitting} />;
    $[23] = form.phone;
    $[24] = submitting;
    $[25] = t8;
    $[26] = t9;
  } else {
    t9 = $[26];
  }
  let t10;
  if ($[27] !== onChange) {
    t10 = v_3 => onChange("company", v_3);
    $[27] = onChange;
    $[28] = t10;
  } else {
    t10 = $[28];
  }
  let t11;
  if ($[29] !== form.company || $[30] !== submitting || $[31] !== t10) {
    t11 = <FormField label="Company Name" id="company" value={form.company} onChange={t10} disabled={submitting} />;
    $[29] = form.company;
    $[30] = submitting;
    $[31] = t10;
    $[32] = t11;
  } else {
    t11 = $[32];
  }
  let t12;
  if ($[33] === Symbol.for("react.memo_cache_sentinel")) {
    t12 = <label htmlFor="message" className="block text-sm font-medium text-white/80">Message</label>;
    $[33] = t12;
  } else {
    t12 = $[33];
  }
  let t13;
  if ($[34] !== onChange) {
    t13 = e => onChange("message", e.target.value);
    $[34] = onChange;
    $[35] = t13;
  } else {
    t13 = $[35];
  }
  let t14;
  if ($[36] !== form.message || $[37] !== submitting || $[38] !== t13) {
    t14 = <div className="space-y-1.5">{t12}<textarea id="message" rows={4} value={form.message} onChange={t13} disabled={submitting} className="w-full rounded-lg bg-white/5 border border-white/10 px-3 py-2 text-white placeholder:text-white/30 focus:outline-none focus:ring-2 focus:ring-cyan-500/50 focus:border-cyan-500/50 disabled:opacity-50 resize-none text-sm" placeholder="Tell us about your requirements..." /></div>;
    $[36] = form.message;
    $[37] = submitting;
    $[38] = t13;
    $[39] = t14;
  } else {
    t14 = $[39];
  }
  let t15;
  if ($[40] !== onTurnstileError || $[41] !== onTurnstileSuccess || $[42] !== turnstileResetKey) {
    t15 = <TurnstileWidget onSuccess={onTurnstileSuccess} onError={onTurnstileError} onExpire={onTurnstileError} resetKey={turnstileResetKey} className="flex justify-center" />;
    $[40] = onTurnstileError;
    $[41] = onTurnstileSuccess;
    $[42] = turnstileResetKey;
    $[43] = t15;
  } else {
    t15 = $[43];
  }
  let t16;
  if ($[44] !== error) {
    t16 = error && <div className="text-sm text-red-400 bg-red-500/10 border border-red-500/20 p-3 rounded-lg">{error}</div>;
    $[44] = error;
    $[45] = t16;
  } else {
    t16 = $[45];
  }
  const t17 = submitting || !captchaToken;
  let t18;
  if ($[46] !== submitting) {
    t18 = submitting ? <><Loader2 className="h-4 w-4 animate-spin" /> Submitting...</> : <><Send className="h-4 w-4" /> Submit Enquiry</>;
    $[46] = submitting;
    $[47] = t18;
  } else {
    t18 = $[47];
  }
  let t19;
  if ($[48] !== t17 || $[49] !== t18) {
    t19 = <button type="submit" disabled={t17} className="w-full flex items-center justify-center gap-2 px-6 py-3 rounded-lg bg-gradient-to-r from-cyan-500 to-teal-500 text-white font-medium hover:from-cyan-600 hover:to-teal-600 transition-all disabled:opacity-50 disabled:cursor-not-allowed">{t18}</button>;
    $[48] = t17;
    $[49] = t18;
    $[50] = t19;
  } else {
    t19 = $[50];
  }
  let t20;
  if ($[51] !== onSubmit || $[52] !== t11 || $[53] !== t14 || $[54] !== t15 || $[55] !== t16 || $[56] !== t19 || $[57] !== t5 || $[58] !== t7 || $[59] !== t9) {
    t20 = <form onSubmit={onSubmit} className="space-y-4">{t5}{t7}{t9}{t11}{t14}{t15}{t16}{t19}</form>;
    $[51] = onSubmit;
    $[52] = t11;
    $[53] = t14;
    $[54] = t15;
    $[55] = t16;
    $[56] = t19;
    $[57] = t5;
    $[58] = t7;
    $[59] = t9;
    $[60] = t20;
  } else {
    t20 = $[60];
  }
  return t20;
}

/* ── Form field helper ────────────────────────────────────────────── */

function FormField(t0) {
  const $ = _c(15);
  const {
    label,
    id,
    type: t1,
    value,
    onChange,
    required,
    disabled
  } = t0;
  const type = t1 === undefined ? "text" : t1;
  let t2;
  if ($[0] !== id || $[1] !== label) {
    t2 = <label htmlFor={id} className="block text-sm font-medium text-white/80">{label}</label>;
    $[0] = id;
    $[1] = label;
    $[2] = t2;
  } else {
    t2 = $[2];
  }
  let t3;
  if ($[3] !== onChange) {
    t3 = e => onChange(e.target.value);
    $[3] = onChange;
    $[4] = t3;
  } else {
    t3 = $[4];
  }
  let t4;
  if ($[5] !== disabled || $[6] !== id || $[7] !== required || $[8] !== t3 || $[9] !== type || $[10] !== value) {
    t4 = <input id={id} type={type} value={value} onChange={t3} required={required} disabled={disabled} className="w-full rounded-lg bg-white/5 border border-white/10 px-3 py-2 text-white placeholder:text-white/30 focus:outline-none focus:ring-2 focus:ring-cyan-500/50 focus:border-cyan-500/50 disabled:opacity-50 text-sm" />;
    $[5] = disabled;
    $[6] = id;
    $[7] = required;
    $[8] = t3;
    $[9] = type;
    $[10] = value;
    $[11] = t4;
  } else {
    t4 = $[11];
  }
  let t5;
  if ($[12] !== t2 || $[13] !== t4) {
    t5 = <div className="space-y-1.5">{t2}{t4}</div>;
    $[12] = t2;
    $[13] = t4;
    $[14] = t5;
  } else {
    t5 = $[14];
  }
  return t5;
}